import React, { useEffect, useMemo, useState } from 'react';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import {
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminSupportingText,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  getSubscriptionHistoryForBusiness,
  formatHistoryDate,
} from './businessSubscriptionHistoryData';

/**
 * Business Billing Record — Subscription History tab.
 * Lifecycle events only (renewals stay in Transactions).
 */
const BusinessBillingHistoryTab = ({ businessId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const historyRows = useMemo(
    () => getSubscriptionHistoryForBusiness(businessId),
    [businessId]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [businessId]);

  const itemsPerPage = CONFIG_LIST_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(historyRows.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [historyRows.length, currentPage, totalPages]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return historyRows.slice(start, start + itemsPerPage);
  }, [historyRows, currentPage, itemsPerPage]);

  const rangeStart = historyRows.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(currentPage * itemsPerPage, historyRows.length);
  const showPagination = historyRows.length > itemsPerPage;

  // Subscription ID | Source | Event | From | To | Outcome | Requested At | Effective At
  const gridTemplateColumns =
    'minmax(110px, 1fr) minmax(80px, 0.7fr) minmax(140px, 1.3fr) minmax(120px, 1.2fr) minmax(140px, 1.35fr) minmax(110px, 1fr) minmax(100px, 0.9fr) minmax(100px, 0.9fr)';

  return (
    <div>
      <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} !mt-0`}>
        {historyRows.length === 0 ? (
          <div className="text-center py-10">
            <AdminSupportingText className="text-mediumgray">
              No subscription history for this business.
            </AdminSupportingText>
          </div>
        ) : (
          <>
            <div
              className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4 items-center"
              style={{ gridTemplateColumns, width: '100%' }}
            >
              <AdminTableHeader>Subscription ID</AdminTableHeader>
              <AdminTableHeader>Source</AdminTableHeader>
              <AdminTableHeader>Event</AdminTableHeader>
              <AdminTableHeader>From</AdminTableHeader>
              <AdminTableHeader>To</AdminTableHeader>
              <AdminTableHeader>Outcome</AdminTableHeader>
              <AdminTableHeader>Requested At</AdminTableHeader>
              <AdminTableHeader>Effective At</AdminTableHeader>
            </div>

            {paginatedRows.map((row) => (
              <div
                key={row.id}
                className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 items-center"
                style={{ gridTemplateColumns, width: '100%' }}
              >
                <div className="flex items-center min-w-0 min-h-8">
                  <AdminTableBody className="truncate">{row.id}</AdminTableBody>
                </div>
                <div className="flex items-center min-h-8">
                  <AdminTableBody>{row.source || '—'}</AdminTableBody>
                </div>
                <div className="flex items-center min-w-0 min-h-8">
                  <AdminTableBody className="truncate" title={row.event}>
                    {row.event}
                  </AdminTableBody>
                </div>
                <div className="flex items-center min-w-0 min-h-8">
                  <AdminTableBody className="truncate" title={row.from}>
                    {row.from || '—'}
                  </AdminTableBody>
                </div>
                <div className="flex items-center min-w-0 min-h-8">
                  <AdminTableBody className="truncate" title={row.to}>
                    {row.to || '—'}
                  </AdminTableBody>
                </div>
                <div className="flex items-center min-h-8">
                  <AdminStatusBadge status={row.outcome} label={row.outcome} />
                </div>
                <div className="flex items-center min-h-8">
                  <AdminTableMeta>
                    {formatHistoryDate(row.requestedAt)}
                  </AdminTableMeta>
                </div>
                <div className="flex items-center min-w-0 min-h-8">
                  <AdminTableMeta className="truncate">
                    {formatHistoryDate(row.effectiveAt)}
                  </AdminTableMeta>
                </div>
              </div>
            ))}
          </>
        )}
      </DashboardMainContainer>

      <ConfigurationListPaginationFooter
        show={showPagination}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        totalCount={historyRows.length}
        currentPage={currentPage}
        totalPages={totalPages}
        onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
        goToPage={setCurrentPage}
      />
    </div>
  );
};

export default BusinessBillingHistoryTab;
